import { t } from 'i18next';
import groupBy from 'lodash-es/groupBy';

import { formatPrice, getPlanTableLabel } from '@/modules/billing/utils';

interface PriceTier {
  unit_amount: number;
  up_to: number | null;
}

interface InvoiceLine {
  id: string;
  amount: number;
  quantity: number;
  period: { start: number; end: number };
  price: { id: string; unit_amount: number | null; nickname: string; tiers?: PriceTier[] };
}

export interface InvoiceTableRow {
  id: string;
  item: string;
  count: string;
  unit_price: string;
  price: string;
}

const buildSingleRow = (
  nickname: string,
  line: InvoiceLine,
  planName: string,
  currency: string
): InvoiceTableRow => ({
  id: nickname,
  item: `${nickname === 'plan' ? `${planName} ` : ''}${getPlanTableLabel(nickname)}`,
  count: String(line.quantity || 0),
  unit_price: formatPrice(line.price.unit_amount ?? 0, currency),
  price: formatPrice(line.amount, currency),
});

const buildTierRows = (
  nickname: string,
  lines: InvoiceLine[],
  currency: string
): InvoiceTableRow[] => {
  const label = getPlanTableLabel(nickname);
  const rows: InvoiceTableRow[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.quantity === 0) {
      continue;
    }

    const unitAmount = line.quantity > 0 ? Math.round(line.amount / line.quantity) : 0;

    rows.push({
      id: `${nickname}-${line.id}`,
      item: `${unitAmount === 0 ? t('billing.free') : t('billing.paid')} ${label}`,
      count: String(line.quantity),
      unit_price: formatPrice(unitAmount, currency),
      price: formatPrice(line.amount, currency),
    });
  }

  return rows;
};

/** Transforms Stripe invoice lines into table rows grouped by period and item type. */
export const parseInvoices = (
  invoiceLines: InvoiceLine[],
  planName: string,
  currency: string
): InvoiceTableRow[] => {
  const byPeriod = groupBy(invoiceLines, (line) =>
    line.period ? `${line.period.start}-${line.period.end}` : ''
  );

  const rows: InvoiceTableRow[] = [];

  for (const periodLines of Object.values(byPeriod)) {
    const byNickname = groupBy(periodLines, 'price.nickname');

    for (const [nickname, lines] of Object.entries(byNickname)) {
      const totalQuantity = lines.reduce((sum, line) => sum + (line.quantity || 0), 0);
      if (totalQuantity === 0) {
        continue;
      }

      if (lines.length === 1) {
        rows.push(buildSingleRow(nickname, lines[0] as InvoiceLine, planName, currency));
      } else {
        rows.push(...buildTierRows(nickname, lines as InvoiceLine[], currency));
      }
    }
  }

  return rows;
};
